import moment from 'moment';
import * as XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';

// pdfmake 0.2.x: pdfFonts.pdfMake.vfs ; pdfmake 0.3.x: pdfFonts è già il vfs.
pdfMake.vfs = (pdfFonts && pdfFonts.pdfMake && pdfFonts.pdfMake.vfs)
	|| (pdfFonts && pdfFonts.vfs)
	|| pdfFonts;

/**
 * Mixin condiviso per esportare una fattura in PDF (template proforma) o XLSX.
 * Espone `exporting` (null | 'pdf' | 'xlsx') per lo stato dei bottoni e
 * `exportInvoiceAs(invoice, format)` come entry point.
 * Richiede che $root.customerMap sia popolato (populateUsefulMaps).
 * Allineato a InvoiceTemplate.vue: valuta da invoice.currency formattata con Intl, nessuna IVA.
 */
export const invoiceExportMixin = {
	data() {
		return {
			exporting: null
		};
	},
	methods: {
		async exportInvoiceAs(invoice, format) {
			if (!invoice || this.exporting) return;
			this.exporting = format;
			try {
				if (format === "xlsx") this.exportInvoiceXlsx(invoice);
				else if (format === "pdf") this.exportInvoicePdf(invoice);
			} catch (err) {
				console.error("Invoice export failed:", err);
				if (this.$root.createToastFunction) {
					this.$root.createToastFunction(this.$t("An error occurred while exporting the billing data."), "alert");
				}
			} finally {
				this.exporting = null;
			}
		},

		// ── helpers ──────────────────────────────────────────────
		invoiceCustomer(invoice) {
			return this.$root.customerMap ? this.$root.customerMap[invoice.id_customer] : null;
		},
		// Codice valuta ISO 4217 della fattura (allineato a InvoiceTemplate.vue):
		// da invoice.currency, fallback al customer e infine EUR.
		invoiceCurrencyCode(invoice, customer) {
			return (invoice && invoice.currency) || (customer && customer.currency) || "EUR";
		},
		// Importo (double) → stringa di prezzo: separatori e simbolo valuta scelti da Intl
		// in base a locale + codice ISO 4217.
		invoiceMoney(value, currencyCode) {
			const amount = Number(value) || 0;
			const locale = ((this.$i18n && this.$i18n.locale) || "en").replace("_", "-");
			try {
				return new Intl.NumberFormat(locale, {
					style: "currency",
					currency: currencyCode
				}).format(amount);
			} catch (e) {
				// codice valuta non riconosciuto da Intl: fallback "1.234,56 XXX"
				const num = new Intl.NumberFormat(locale, {
					minimumFractionDigits: 2,
					maximumFractionDigits: 2
				}).format(amount);
				return num + " " + (currencyCode || "");
			}
		},
		invoiceBuildFilename(invoice, ext) {
			const ref = invoice.external_invoice_id || invoice.id || "draft";
			return "invoice_" + ref + "_" + moment().format("YYYYMMDD_HHmmss") + "." + ext;
		},
		invoiceTicketDuration(ticket) {
			if (!ticket || !Array.isArray(ticket.sessions)) return 0;
			return ticket.sessions.reduce((sum, s) => sum + (Number(s.total_time) || 0), 0);
		},
		// secondi → "Xh Ym Zs" (secondi residui solo sotto 1h)
		invoiceFormatHours(seconds) {
			if (seconds == null) return "—";
			const s = Math.floor(Number(seconds));
			if (s === 0) return "0s";
			const h = Math.floor(s / 3600);
			const m = Math.floor((s % 3600) / 60);
			const sec = s % 60;
			if (h > 0) return h + "h" + (m > 0 ? " " + m + "m" : "");
			if (m > 0) return m + "m" + (sec > 0 ? " " + sec + "s" : "");
			return sec + "s";
		},
		invoiceDecimalHours(seconds) {
			if (seconds == null) return "0.00";
			return (Number(seconds) / 3600).toFixed(2);
		},

		// ── XLSX (foglio unico che simula il template proforma) ──
		exportInvoiceXlsx(invoice) {
			const customer = this.invoiceCustomer(invoice);
			const auth = this.$root.authObj || {};
			const currency = this.invoiceCurrencyCode(invoice, customer);
			const money = (v) => this.invoiceMoney(v, currency);
			const hourlyRate = customer && customer.hourly_rate != null ? Number(customer.hourly_rate) : 0;

			const subtotal = invoice.total_amount != null
				? Number(invoice.total_amount)
				: (invoice.tickets || []).reduce((sum, t) => sum + hourlyRate * (this.invoiceTicketDuration(t) / 3600), 0);

			// stili cella (xlsx-js-style)
			const TICKET_HEADER_STYLE = {
				fill: { patternType: "solid", fgColor: { rgb: "808080" } },
				font: { bold: true, color: { rgb: "FFFFFF" } }
			};
			const SESSION_HEADER_STYLE = {
				fill: { patternType: "solid", fgColor: { rgb: "EEF0F2" } },
				font: { bold: true, color: { rgb: "333333" } }
			};
			const TITLE_STYLE = { font: { bold: true, sz: 18 } };
			const SECTION_STYLE = { font: { bold: true, sz: 11, color: { rgb: "888888" } } };
			const TOTAL_STYLE = { font: { bold: true } };
			const FOOTER_STYLE = { font: { italic: true, color: { rgb: "888888" } }, alignment: { horizontal: "center" } };
			const hCell = (text, style) => ({ v: text, s: style });

			const NCOLS = 5; // A-E
			const aoa = [];
			const merges = [];
			// aggiunge una riga con testo nella prima cella, mergiata su tutte le colonne
			const pushFullRow = (text, style) => {
				const r = aoa.length;
				const row = new Array(NCOLS).fill("");
				row[0] = style ? hCell(text, style) : text;
				aoa.push(row);
				merges.push({ s: { r, c: 0 }, e: { r, c: NCOLS - 1 } });
			};
			const blank = () => aoa.push(new Array(NCOLS).fill(""));

			// ── Header documento ──
			pushFullRow(this.$t("PROFORMA INVOICE"), TITLE_STYLE);
			aoa.push([this.$t("Document number") + ":", invoice.external_invoice_id || ("#" + (invoice.id || "—")), "", "", ""]);
			aoa.push([this.$t("Date") + ":", invoice.inserted_at ? moment.utc(invoice.inserted_at).local().format("DD/MM/YYYY") : "—", "", "", ""]);
			aoa.push([this.$t("Invoiced") + ":", invoice.invoiced ? this.$t("Invoiced") : this.$t("Not invoiced"), "", "", ""]);
			blank();

			// ── Emittente ──
			pushFullRow(this.$t("Issued by").toUpperCase(), SECTION_STYLE);
			aoa.push([auth.company || auth.ma_username || "—", "", "", "", ""]);
			blank();

			// ── Destinatario ──
			pushFullRow(this.$t("Bill to").toUpperCase(), SECTION_STYLE);
			if (customer) {
				aoa.push([customer.company_name || "—", "", "", "", ""]);
				if (customer.vat_number) aoa.push([this.$t("VAT") + ": " + customer.vat_number, "", "", "", ""]);
				const addr = [
					customer.address,
					[customer.postal_code, customer.city, customer.province].filter(Boolean).join(' '),
					customer.country
				].filter(Boolean).join(', ');
				if (addr) aoa.push([addr, "", "", "", ""]);
				if (customer.pec_email) aoa.push(["PEC: " + customer.pec_email, "", "", "", ""]);
				if (customer.sdi_code) aoa.push(["SDI: " + customer.sdi_code, "", "", "", ""]);
			} else {
				aoa.push(["—", "", "", "", ""]);
			}
			blank();

			// ── Dettaglio: header + ticket con sotto-righe sessioni ──
			aoa.push([
				hCell("#", TICKET_HEADER_STYLE),
				hCell(this.$t("Ticket"), TICKET_HEADER_STYLE),
				hCell(this.$t("Hours"), TICKET_HEADER_STYLE),
				hCell(this.$t("Unit price"), TICKET_HEADER_STYLE),
				hCell(this.$t("Amount"), TICKET_HEADER_STYLE)
			]);
			if ((invoice.tickets || []).length) {
				invoice.tickets.forEach((t, i) => {
					const durationSec = this.invoiceTicketDuration(t);
					aoa.push([
						i + 1,
						t.title || "—",
						this.invoiceDecimalHours(durationSec),
						money(hourlyRate),
						money(hourlyRate * (durationSec / 3600))
					]);
					if (t.description) aoa.push(["", t.description, "", "", ""]);

					const sessions = t.sessions || [];
					aoa.push(["", this.$t("Sessions") + " (" + sessions.length + ")", "", "", ""]);
					if (sessions.length) {
						aoa.push([
							"",
							hCell(this.$t("Group"), SESSION_HEADER_STYLE),
							hCell(this.$t("Computer"), SESSION_HEADER_STYLE),
							"",
							hCell(this.$t("Duration"), SESSION_HEADER_STYLE)
						]);
						sessions.forEach(s => {
							aoa.push([
								"",
								s.group_name || "—",
								s.custom_name || s.name_computer_host || "—",
								"",
								this.invoiceFormatHours(s.total_time)
							]);
						});
					} else {
						aoa.push(["", this.$t("No sessions for this ticket."), "", "", ""]);
					}
					blank();
				});
			} else {
				pushFullRow(this.$t("No tickets for this invoice."));
			}

			// ── Totali (allineati a destra: colonne D-E) ──
			aoa.push(["", "", "", this.$t("Subtotal") + ":", money(subtotal)]);
			aoa.push(["", "", "", this.$t("Tax") + ":", "—"]);
			aoa.push(["", "", "", hCell(this.$t("Total") + ":", TOTAL_STYLE), hCell(money(subtotal), TOTAL_STYLE)]);

			// ── Note ──
			if (invoice.note) {
				blank();
				pushFullRow(this.$t("Notes"), SECTION_STYLE);
				pushFullRow(invoice.note);
			}

			// ── Footer disclaimer ──
			blank();
			pushFullRow(this.$t("This document is not a fiscal invoice."), FOOTER_STYLE);

			const ws = XLSX.utils.aoa_to_sheet(aoa);
			ws["!cols"] = [{ wch: 6 }, { wch: 48 }, { wch: 16 }, { wch: 16 }, { wch: 16 }];
			ws["!merges"] = merges;

			const wb = XLSX.utils.book_new();
			XLSX.utils.book_append_sheet(wb, ws, this.$t("Invoice"));

			const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
			saveAs(new Blob([out], { type: "application/octet-stream" }), this.invoiceBuildFilename(invoice, "xlsx"));
		},

		// ── PDF (template proforma) ──────────────────────────────
		exportInvoicePdf(invoice) {
			const customer = this.invoiceCustomer(invoice);
			const auth = this.$root.authObj || {};
			const currency = this.invoiceCurrencyCode(invoice, customer);
			const money = (v) => this.invoiceMoney(v, currency);
			const hourlyRate = customer && customer.hourly_rate != null ? Number(customer.hourly_rate) : 0;

			const fmtDate = (d) => d ? moment.utc(d).local().format("DD/MM/YYYY") : "—";
			const ticketAmount = (t) => hourlyRate * (this.invoiceTicketDuration(t) / 3600);

			const subtotal = invoice.total_amount != null
				? Number(invoice.total_amount)
				: (invoice.tickets || []).reduce((sum, t) => sum + ticketAmount(t), 0);

			// ── DETAIL TABLE (ticket + sotto-tabella sessioni) ──────
			const detailHeader = [
				{ text: "#", style: "tableHeader" },
				{ text: this.$t("Ticket"), style: "tableHeader" },
				{ text: this.$t("Hours"), style: "tableHeader", alignment: "right" },
				{ text: this.$t("Unit price"), style: "tableHeader", alignment: "right" },
				{ text: this.$t("Amount"), style: "tableHeader", alignment: "right" }
			];
			const detailBody = [detailHeader];

			if ((invoice.tickets || []).length) {
				invoice.tickets.forEach((t, i) => {
					// riga ticket
					detailBody.push([
						{ text: String(i + 1) },
						{
							stack: [
								{ text: t.title || "—", bold: true },
								t.description ? { text: t.description, fontSize: 9, color: "#666", margin: [0, 2, 0, 0] } : null
							].filter(Boolean)
						},
						{ text: this.invoiceDecimalHours(this.invoiceTicketDuration(t)), alignment: "right" },
						{ text: money(hourlyRate), alignment: "right" },
						{ text: money(ticketAmount(t)), alignment: "right", bold: true }
					]);

					// riga sessioni (sotto-tabella in colSpan)
					const sessions = t.sessions || [];
					const sessionsContent = sessions.length
						? {
							table: {
								widths: ["*", "*", "auto"],
								body: [
									[
										{ text: this.$t("Group"), bold: true, fontSize: 8, fillColor: "#eef0f2" },
										{ text: this.$t("Computer"), bold: true, fontSize: 8, fillColor: "#eef0f2" },
										{ text: this.$t("Duration"), bold: true, fontSize: 8, fillColor: "#eef0f2", alignment: "right" }
									],
									...sessions.map(s => [
										{ text: s.group_name || "—", fontSize: 8 },
										{ text: s.custom_name || s.name_computer_host || "—", fontSize: 8 },
										{ text: this.invoiceFormatHours(s.total_time), fontSize: 8, alignment: "right" }
									])
								]
							},
							layout: "lightHorizontalLines"
						}
						: { text: this.$t("No sessions for this ticket."), italics: true, fontSize: 8, color: "#888" };

					detailBody.push([
						{ text: "" },
						{
							colSpan: 4,
							stack: [
								{ text: this.$t("Sessions") + " (" + sessions.length + ")", fontSize: 8, bold: true, color: "#555", margin: [0, 0, 0, 3] },
								sessionsContent
							],
							fillColor: "#f8f9fa"
						},
						{}, {}, {}
					]);
				});
			} else {
				detailBody.push([{ colSpan: 5, text: this.$t("No tickets for this invoice."), italics: true, color: "#888", alignment: "center", margin: [0, 8, 0, 8] }, {}, {}, {}, {}]);
			}

			// ── ISSUER / CUSTOMER ───────────────────────────────────
			const issuerLines = [
				{ text: auth.company || auth.ma_username || "—", bold: true, fontSize: 11 }
			];

			const customerLines = [];
			if (customer) {
				customerLines.push({ text: customer.company_name || "—", bold: true, fontSize: 11 });
				if (customer.vat_number) customerLines.push({ text: this.$t("VAT") + ": " + customer.vat_number, fontSize: 9, color: "#444" });
				const addr = [
					customer.address,
					[customer.postal_code, customer.city, customer.province].filter(Boolean).join(' '),
					customer.country
				].filter(Boolean).join(', ');
				if (addr) customerLines.push({ text: addr, fontSize: 9, color: "#444" });
				if (customer.pec_email) customerLines.push({ text: "PEC: " + customer.pec_email, fontSize: 9, color: "#444" });
				if (customer.sdi_code) customerLines.push({ text: "SDI: " + customer.sdi_code, fontSize: 9, color: "#444" });
			} else {
				customerLines.push({ text: "—", fontSize: 11 });
			}

			const partyBoxLayout = {
				hLineWidth: () => 1, vLineWidth: () => 1,
				hLineColor: () => "#e2e8f0", vLineColor: () => "#e2e8f0",
				paddingLeft: () => 10, paddingRight: () => 10,
				paddingTop: () => 8, paddingBottom: () => 8,
				fillColor: () => "#fafbfc"
			};

			// ── TOTALS (nessuna IVA: Tax = —, Total = subtotal) ─────
			const totalsTable = {
				widths: ["*", "auto"],
				body: [
					[
						{ text: this.$t("Subtotal") + ":", fontSize: 10, color: "#555", border: [false, false, false, false] },
						{ text: money(subtotal), fontSize: 10, alignment: "right", border: [false, false, false, false] }
					],
					[
						{ text: this.$t("Tax") + ":", fontSize: 10, color: "#555", border: [false, false, false, false] },
						{ text: "—", fontSize: 10, alignment: "right", border: [false, false, false, false] }
					],
					[
						{ text: this.$t("Total") + ":", fontSize: 13, bold: true, border: [false, true, false, true] },
						{ text: money(subtotal), fontSize: 13, bold: true, alignment: "right", border: [false, true, false, true] }
					]
				]
			};

			// ── DOC DEFINITION ──────────────────────────────────────
			const docDefinition = {
				pageMargins: [40, 40, 40, 50],
				content: [
					{
						columns: [
							{
								stack: [
									{ text: this.$t("PROFORMA INVOICE"), fontSize: 22, bold: true, characterSpacing: 2 },
									{ text: " ", fontSize: 4 },
									{ text: this.$t("Document number") + ": " + (invoice.external_invoice_id || ("#" + (invoice.id || "—"))), fontSize: 10, color: "#444" },
									{ text: this.$t("Date") + ": " + fmtDate(invoice.inserted_at), fontSize: 10, color: "#444" }
								]
							},
							{
								width: "auto",
								alignment: "right",
								table: {
									body: [[{
										text: invoice.invoiced ? this.$t("Invoiced") : this.$t("Not invoiced"),
										fillColor: invoice.invoiced ? "#22c55e" : "#f59e0b",
										color: "#fff",
										bold: true,
										fontSize: 10,
										margin: [8, 4, 8, 4]
									}]]
								},
								layout: "noBorders"
							}
						]
					},
					{ canvas: [{ type: "line", x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1.5, lineColor: "#222" }], margin: [0, 6, 0, 16] },
					{
						columns: [
							{
								table: {
									widths: ["*"],
									body: [
										[{ text: this.$t("Issued by").toUpperCase(), fontSize: 9, color: "#888", margin: [0, 0, 0, 4] }],
										[{ stack: issuerLines }]
									]
								},
								layout: partyBoxLayout
							},
							{ width: 12, text: "" },
							{
								table: {
									widths: ["*"],
									body: [
										[{ text: this.$t("Bill to").toUpperCase(), fontSize: 9, color: "#888", margin: [0, 0, 0, 4] }],
										[{ stack: customerLines }]
									]
								},
								layout: partyBoxLayout
							}
						],
						margin: [0, 0, 0, 16]
					},
					{
						table: {
							widths: [22, "*", 50, 75, 80],
							headerRows: 1,
							body: detailBody
						},
						layout: {
							fillColor: (rowIndex) => rowIndex === 0 ? "#222" : null,
							hLineWidth: () => 0.5,
							vLineWidth: () => 0,
							hLineColor: () => "#e2e8f0",
							paddingTop: () => 6,
							paddingBottom: () => 6,
							paddingLeft: () => 8,
							paddingRight: () => 8
						},
						margin: [0, 0, 0, 16]
					},
					{
						columns: [
							{ text: "", width: "*" },
							{
								width: 240,
								table: totalsTable,
								layout: {
									hLineWidth: (i) => (i === 2 || i === 3) ? 2 : 0,
									vLineWidth: () => 0,
									hLineColor: () => "grey",
									paddingTop: () => 4,
									paddingBottom: () => 4
								}
							}
						],
						margin: [0, 0, 0, 16]
					},
					invoice.note ? {
						stack: [
							{ text: this.$t("Notes"), bold: true, fontSize: 11, margin: [0, 0, 0, 4] },
							{
								table: {
									widths: ["*"],
									body: [[{ text: invoice.note, fontSize: 10, fillColor: "#fffbe6", margin: [8, 6, 8, 6] }]]
								},
								layout: {
									hLineWidth: () => 1, vLineWidth: () => 1,
									hLineColor: () => "#f3e8a8", vLineColor: () => "#f3e8a8"
								}
							}
						],
						margin: [0, 0, 0, 16]
					} : null,
					{ canvas: [{ type: "line", x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.5, lineColor: "#e2e8f0" }], margin: [0, 4, 0, 6] },
					{ text: this.$t("This document is not a fiscal invoice."), fontSize: 9, italics: true, color: "#888", alignment: "center" }
				].filter(Boolean),
				styles: {
					tableHeader: { color: "black", bold: true, fontSize: 10 }
				},
				defaultStyle: { fontSize: 10 }
			};

			pdfMake.createPdf(docDefinition).download(this.invoiceBuildFilename(invoice, "pdf"));
		}
	}
};
